/**
 * Guardrails Hooks
 * Jotai atoms and hooks for guardrail management
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { guardrailService, guardrailFallbackService } from '@/services';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import type { CreateGuardrailDto, UpdateGuardrailDto, CreateGuardrailFallbackDto, Guardrail } from '@/types';

// Query atoms
export const systemGuardrailsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['guardrails', 'system', buScopeKey(get)],
  queryFn: () => guardrailService.getSystemGuardrails(buScopeParams(get)),
  refetchOnWindowFocus: true
}));

export const customGuardrailsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['guardrails', 'custom', buScopeKey(get)],
  queryFn: () => guardrailService.getCustomGuardrails(buScopeParams(get)),
  refetchOnWindowFocus: true
}));

export const activeGuardrailsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['guardrails', 'active', buScopeKey(get)],
  queryFn: () => guardrailService.getActiveGuardrails(buScopeParams(get)),
  refetchOnWindowFocus: false
}));

// The count above the table has to agree with the table, so stats carry the same scope.
export const guardrailsStatsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['guardrails', 'stats', buScopeKey(get)],
  queryFn: () => guardrailService.getGuardrailStats(buScopeParams(get)),
  refetchOnWindowFocus: false
}));

// Creating a fallback already sends the brand, so the list has to read by it too —
// otherwise a fallback made under one brand stays visible under every other one.
export const guardrailFallbacksQueryAtom = atomWithQuery((get) => ({
  queryKey: ['guardrails', 'fallback', buScopeKey(get)],
  queryFn: () => guardrailFallbackService.getGuardrailFallbacks(undefined, buScopeParams(get)),
  refetchOnWindowFocus: false
}));

// The count above the table has to agree with the table, so stats carry the same scope.
export const guardrailFallbackStatsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['guardrails', 'fallback', 'stats', buScopeKey(get)],
  queryFn: () => guardrailFallbackService.getGuardrailFallbackStats(buScopeParams(get)),
  refetchOnWindowFocus: false
}));

// Mutation atoms
export const createGuardrailMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['guardrails', 'create'],
  mutationFn: (data: CreateGuardrailDto) => guardrailService.createGuardrail(data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['guardrails']);
  }
}));

export const updateGuardrailMutationAtom = atomWithMutation(() => ({
  mutationKey: ['guardrails', 'update'],
  mutationFn: ({ id, data }: { id: string; data: UpdateGuardrailDto }) => guardrailService.updateGuardrail(id, data),
  onMutate: async ({ id, data }) => {
    await queryClient.cancelQueries({ queryKey: ['guardrails'] });

    const previousCustomGuardrails = queryClient.getQueryData<Guardrail[]>(['guardrails', 'custom']);
    const previousSystemGuardrails = queryClient.getQueryData<Guardrail[]>(['guardrails', 'system']);

    const applyUpdate = (old: Guardrail[] | undefined) =>
      old?.map((guardrail) => (guardrail.id === id ? { ...guardrail, ...data } : guardrail));

    if (previousCustomGuardrails) {
      queryClient.setQueryData<Guardrail[]>(['guardrails', 'custom'], applyUpdate);
    }
    if (previousSystemGuardrails) {
      queryClient.setQueryData<Guardrail[]>(['guardrails', 'system'], applyUpdate);
    }

    return { previousCustomGuardrails, previousSystemGuardrails };
  },
  onError: (_error, _variables, context) => {
    if (context?.previousCustomGuardrails) {
      queryClient.setQueryData(['guardrails', 'custom'], context.previousCustomGuardrails);
    }
    if (context?.previousSystemGuardrails) {
      queryClient.setQueryData(['guardrails', 'system'], context.previousSystemGuardrails);
    }
  },
  onSettled: () => {
    // Always refetch after error or success to ensure cache is in sync
    invalidateQueries(['guardrails']);
  }
}));

export const deleteGuardrailMutationAtom = atomWithMutation(() => ({
  mutationKey: ['guardrails', 'delete'],
  mutationFn: (id: string) => guardrailService.deleteGuardrail(id),
  onSuccess: () => {
    invalidateQueries(['guardrails']);
  }
}));

export const createGuardrailFallbackMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['guardrails', 'fallback', 'create'],
  mutationFn: (data: CreateGuardrailFallbackDto) =>
    guardrailFallbackService.createGuardrailFallback(data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['guardrails', 'fallback']);
  }
}));

export const deleteGuardrailFallbackMutationAtom = atomWithMutation(() => ({
  mutationKey: ['guardrails', 'fallback', 'delete'],
  mutationFn: (id: string) => guardrailFallbackService.deleteGuardrailFallback(id),
  onSuccess: () => {
    invalidateQueries(['guardrails', 'fallback']);
  }
}));

// Hooks
export function useSystemGuardrails() {
  const [systemGuardrailsQuery] = useAtom(systemGuardrailsQueryAtom);
  return systemGuardrailsQuery;
}

export function useCustomGuardrails() {
  const [customGuardrailsQuery] = useAtom(customGuardrailsQueryAtom);
  return customGuardrailsQuery;
}

export function useActiveGuardrails() {
  const [activeGuardrailsQuery] = useAtom(activeGuardrailsQueryAtom);
  return activeGuardrailsQuery;
}

export function useGuardrailsStats() {
  const [guardrailsStatsQuery] = useAtom(guardrailsStatsQueryAtom);
  return guardrailsStatsQuery;
}

export function useGuardrailFallbacks() {
  const [guardrailFallbacksQuery] = useAtom(guardrailFallbacksQueryAtom);
  return guardrailFallbacksQuery;
}

export function useGuardrailFallbackStats() {
  const [guardrailFallbackStatsQuery] = useAtom(guardrailFallbackStatsQueryAtom);
  return guardrailFallbackStatsQuery;
}

export function useCreateGuardrail() {
  const [createGuardrailMutation] = useAtom(createGuardrailMutationAtom);
  return createGuardrailMutation;
}

export function useUpdateGuardrail() {
  const [updateGuardrailMutation] = useAtom(updateGuardrailMutationAtom);
  return updateGuardrailMutation;
}

export function useDeleteGuardrail() {
  const [deleteGuardrailMutation] = useAtom(deleteGuardrailMutationAtom);
  return deleteGuardrailMutation;
}

export function useCreateGuardrailFallback() {
  const [createGuardrailFallbackMutation] = useAtom(createGuardrailFallbackMutationAtom);
  return createGuardrailFallbackMutation;
}

export function useDeleteGuardrailFallback() {
  const [deleteGuardrailFallbackMutation] = useAtom(deleteGuardrailFallbackMutationAtom);
  return deleteGuardrailFallbackMutation;
}
